// `excel_scan_sheet` — the compact first pass over a worksheet (domain #32 / D2). Pure: no Office.js.
//
// The executor reads a window of rows (values, R1C1 formulas, number formats) and this module turns
// it into what the agents need to reason about a sheet: per row a label, a kind, ONE R1C1 pattern
// and the count of cells breaking it; the sheet timeline; the label column; and deterministic
// issues. Cell values never leave Excel in bulk — a 60-period row becomes one pattern and a sample.
import { colToLetter } from './a1';
import { formulaLiterals } from './legacy';
import type { CellValue } from '../../types/excel';

export type ScanRowKind = 'timeline' | 'header' | 'input' | 'calc' | 'total' | 'check' | 'blank' | 'other';
export type Periodicity = 'monthly' | 'quarterly' | 'semi' | 'annual' | 'other';

export interface ScanGrid {
  /** 1-based absolute worksheet row of `values[0]`. */
  startRow: number;
  /** 1-based absolute worksheet column of `values[0][0]`. */
  startCol: number;
  values: CellValue[][];
  /** Office.js `formulasR1C1`: the R1C1 formula for formula cells, the plain value otherwise. */
  formulasR1C1: unknown[][];
  numberFormats: string[][];
  /** Row hidden flags (`getRowProperties({rowHidden: true})`, ExcelApi 1.9), one per grid row; absent when unavailable. */
  rowHidden?: boolean[];
}

export interface ScanTimeline {
  header_row: number;
  first_col: string;
  last_col: string;
  periods: number;
  periodicity: Periodicity | null;
  kind: 'dates' | 'numbers' | 'labels';
}

export interface ScanRow {
  n: number;
  /** Last row of a collapsed blank span (`include_blank`). */
  to?: number;
  label: string | null;
  unit?: string | null;
  kind: ScanRowKind;
  /** Majority R1C1 pattern of the row over the axis, `const` for inputs, absent for text rows. */
  pattern?: string | null;
  /** Non-blank axis cells whose R1C1 differs from `pattern` (a literal in a formula row counts). */
  exceptions?: number;
  first?: CellValue;
  fmt?: string | null;
  cells?: number;
  /** Row hidden in Excel (false when the host could not report the flags). */
  hidden?: boolean;
  /** Blank cells between the first and the last populated axis cell (holes in the series). */
  gaps?: number;
  /** Extremes of the numeric axis values; null when the row holds no number. */
  min?: number | null;
  max?: number | null;
  /** 1-based period index (from the axis first column) of the first / last non-zero numeric cell; null when none. */
  nonzero_from?: number | null;
  nonzero_to?: number | null;
  /** All populated axis cells share one number format. */
  fmt_uniform?: boolean;
  /** The ONLY off-pattern cell of a formula row is the first period (seed exemption, A1 One formula per row). */
  first_period_exception?: boolean;
}

export type ScanIssueKind = 'error_value' | 'broken_pattern' | 'literal_in_calc' | 'external_link' | 'text_number';

export interface ScanIssue {
  kind: ScanIssueKind;
  row: number;
  address: string;
  detail: string;
}

/** A period-like run (dates, consecutive integers, FY labels) on a row other than the timeline header. */
export interface ScanPeriodRun {
  row: number;
  first_col: string;
  last_col: string;
  periods: number;
}

export interface ScanSheetOutput {
  sheet: string;
  used_range: string | null;
  row_from: number;
  row_to: number;
  next_row: number | null;
  truncated: boolean;
  timeline: ScanTimeline | null;
  label_col: string | null;
  unit_col: string | null;
  rows: ScanRow[];
  issues: ScanIssue[];
  /** Second timelines / index rows found in the window (≥ 4 cells), excluding the timeline header. */
  other_period_runs: ScanPeriodRun[];
  /** Hidden row spans inside the scanned window ("5:7"). */
  hidden_rows: string[];
  stats: { rows_scanned: number; blank_rows: number; input_rows: number; calc_rows: number; issues: number };
}

export const SCAN_DEFAULT_ROWS = 150;
export const SCAN_MAX_ROWS = 300;
/** Rows read from the top of the sheet to detect the timeline when the window starts lower. */
export const SCAN_HEAD_ROWS = 20;
/** Widest window read per pass (label columns + periods). */
export const SCAN_MAX_COLS = 260;
/** Output budget: rows are dropped from the end (and `next_row` set) beyond this many JSON chars. */
export const SCAN_MAX_CHARS = 15_000;
const MAX_ISSUES = 60;
const MIN_TIMELINE_RUN = 4;
const MAX_OTHER_RUNS = 10;

const ERROR_RE = /^#(REF!|DIV\/0!|VALUE!|NAME\?|N\/A|NUM!|NULL!|SPILL!|CALC!)$/;
/** External workbook reference: a bracket that is not an R1C1 offset (`[-1]`), followed by a sheet
 *  name (possibly quoted) and `!` — `[Book.xlsx]Sheet!R1C1`, `'[Book]Sheet name'!A1`. Structured
 *  references (`Table1[Amount]`) carry no `!` after the bracket and are not matched. */
const EXTERNAL_REF_RE = /\[(?![-+]?\d+\])[^\]]+\][^\]\[!*/?:()+,;=<>&^]*'?!/;
/** A string that reads as a number once spaces are removed: "1 250", "12.5", "3,4", "1,250,000", "12%". */
const TEXT_NUMBER_RE = /^[-+]?(?:\d+(?:[.,]\d+)?|\d{1,3}(?:,\d{3})+(?:\.\d+)?)%?$/;

/** True when a formula (A1 or R1C1) references another workbook. String literals are ignored. */
export function hasExternalRef(formula: unknown): boolean {
  if (typeof formula !== 'string' || !formula.startsWith('=')) return false;
  return EXTERNAL_REF_RE.test(formula.replace(/"[^"]*"/g, '""'));
}

/** True for a text cell whose content parses as a number ("1 250", "12.5", "3,4"). */
export function isTextNumber(v: unknown): boolean {
  if (typeof v !== 'string') return false;
  const s = v.replace(/\s/g, ''); // \s covers NBSP / thin spaces used as thousands separators
  return s.length > 0 && TEXT_NUMBER_RE.test(s);
}

/** Collapse hidden flags into spans: rows → "5:7", columns → "D:E". `start` is the 1-based index of `flags[0]`. */
export function hiddenSpans(flags: readonly boolean[] | undefined, start: number, kind: 'row' | 'col'): string[] {
  if (!flags?.length) return [];
  const label = (i: number) => (kind === 'row' ? String(i) : colToLetter(i));
  const out: string[] = [];
  let from: number | null = null;
  for (let i = 0; i <= flags.length; i++) {
    const hidden = i < flags.length && !!flags[i];
    if (hidden && from === null) from = start + i;
    if (!hidden && from !== null) {
      out.push(`${label(from)}:${label(start + i - 1)}`);
      from = null;
    }
  }
  return out;
}
const TOTAL_LABEL_RE = /\b(total|sum|closing|ending|cl[oô]ture|solde|fin de p[ée]riode)\b/i;
const CHECK_LABEL_RE = /\b(check|contr[oô]le|balance|diff(erence)?|[ée]cart|error|erreur|ok)\b/i;
const PERIOD_LABEL_RE = /^(FY|Y|P|H|Q|S|M)\s?-?\d{1,4}$|^\d{4}$|^(year|ann[ée]e|period|p[ée]riode)\s?\d+$/i;

const isBlank = (v: CellValue | undefined): boolean => v === null || v === undefined || v === '';
const isFormula = (f: unknown): f is string => typeof f === 'string' && f.startsWith('=');
const isDateFormat = (fmt: string | undefined): boolean =>
  !!fmt && fmt !== 'General' && /[dDyY]/.test(fmt.replace(/"[^"]*"/g, '').replace(/\[[^\]]*\]/g, '')) && !/[#0]/.test(fmt);

function cellAt(grid: ScanGrid, r: number, c: number): { v: CellValue; f: unknown; fmt: string } {
  return { v: grid.values[r]?.[c] ?? null, f: grid.formulasR1C1[r]?.[c], fmt: grid.numberFormats[r]?.[c] ?? 'General' };
}

function roundSig(n: number, sig = 6): number {
  if (n === 0 || !Number.isFinite(n)) return n;
  const p = Math.pow(10, sig - Math.ceil(Math.log10(Math.abs(n))));
  return Math.round(n * p) / p;
}

// ── Timeline ────────────────────────────────────────────────────────────────

function periodicityOf(deltas: number[]): Periodicity | null {
  if (!deltas.length) return null;
  const sorted = [...deltas].sort((a, b) => a - b);
  const med = sorted[Math.floor(sorted.length / 2)];
  if (med >= 27 && med <= 32) return 'monthly';
  if (med >= 88 && med <= 93) return 'quarterly';
  if (med >= 180 && med <= 185) return 'semi';
  if (med >= 364 && med <= 367) return 'annual';
  return 'other';
}

/** Longest run of period-like cells on one row: dates (serials with a date format), consecutive
 *  integers (years or period indices) or period labels (FY24, Y1, Q3, 2027). */
function periodRun(grid: ScanGrid, r: number): { start: number; len: number; kind: ScanTimeline['kind']; periodicity: Periodicity | null } | null {
  const cols = grid.values[r]?.length ?? 0;
  let best: { start: number; len: number; kind: ScanTimeline['kind']; periodicity: Periodicity | null } | null = null;
  let c = 0;
  while (c < cols) {
    const { v, fmt } = cellAt(grid, r, c);
    let kind: ScanTimeline['kind'] | null = null;
    if (typeof v === 'number' && isDateFormat(fmt)) kind = 'dates';
    else if (typeof v === 'number' && Number.isInteger(v) && v >= 0) kind = 'numbers';
    else if (typeof v === 'string' && PERIOD_LABEL_RE.test(v.trim())) kind = 'labels';
    if (!kind) {
      c++;
      continue;
    }
    const start = c;
    const serials: number[] = [];
    let prevNum: number | null = null;
    while (c < cols) {
      const cell = cellAt(grid, r, c);
      const v2 = cell.v;
      if (kind === 'dates' && typeof v2 === 'number' && isDateFormat(cell.fmt)) serials.push(v2);
      else if (kind === 'numbers' && typeof v2 === 'number' && Number.isInteger(v2) && (prevNum === null || v2 === prevNum + 1)) prevNum = v2;
      else if (kind === 'labels' && typeof v2 === 'string' && PERIOD_LABEL_RE.test(v2.trim())) { /* ok */ }
      else break;
      c++;
    }
    const len = c - start;
    if (len >= MIN_TIMELINE_RUN && (!best || len > best.len)) {
      const deltas = serials.slice(1).map((s, i) => s - serials[i]);
      const periodicity = kind === 'dates' ? periodicityOf(deltas) : kind === 'numbers' && prevNum !== null && prevNum > 1900 ? 'annual' : null;
      best = { start, len, kind, periodicity };
    }
  }
  return best;
}

/** Detect the sheet timeline in the top rows of a grid (the widest period run wins). */
export function detectTimeline(grid: ScanGrid, maxRows = SCAN_HEAD_ROWS): ScanTimeline | null {
  let best: { r: number; run: NonNullable<ReturnType<typeof periodRun>> } | null = null;
  for (let r = 0; r < Math.min(grid.values.length, maxRows); r++) {
    const run = periodRun(grid, r);
    if (run && (!best || run.len > best.run.len)) best = { r, run };
  }
  if (!best) return null;
  return {
    header_row: grid.startRow + best.r,
    first_col: colToLetter(grid.startCol + best.run.start),
    last_col: colToLetter(grid.startCol + best.run.start + best.run.len - 1),
    periods: best.run.len,
    periodicity: best.run.periodicity,
    kind: best.run.kind,
  };
}

// ── Columns ─────────────────────────────────────────────────────────────────

/** Label column = the column left of the axis with the most text cells (leftmost on ties). */
function detectLabelCol(grid: ScanGrid, axisStart: number): number | null {
  const limit = axisStart > 0 ? axisStart : Math.min(5, grid.values[0]?.length ?? 0);
  let best: { c: number; n: number } | null = null;
  for (let c = 0; c < limit; c++) {
    let n = 0;
    for (let r = 0; r < grid.values.length; r++) if (typeof grid.values[r]?.[c] === 'string' && (grid.values[r][c] as string).trim()) n++;
    if (n > 0 && (!best || n > best.n)) best = { c, n };
  }
  return best ? best.c : null;
}

/** Unit column = a column between the label column and the axis whose texts are short (`MWh`, `€/MWh`, `%`). */
function detectUnitCol(grid: ScanGrid, labelCol: number | null, axisStart: number): number | null {
  if (labelCol === null) return null;
  for (let c = labelCol + 1; c < axisStart; c++) {
    let texts = 0;
    let short = 0;
    for (let r = 0; r < grid.values.length; r++) {
      const v = grid.values[r]?.[c];
      if (typeof v === 'string' && v.trim()) {
        texts++;
        if (v.trim().length <= 12) short++;
      }
    }
    if (texts >= 2 && short / texts >= 0.8) return c;
  }
  return null;
}

// ── Formulas ────────────────────────────────────────────────────────────────

/** Numeric literals typed into an R1C1 formula, excluding references, function names and strings. */
export { formulaLiterals };

function majority(patterns: string[]): { pattern: string; count: number } | null {
  if (!patterns.length) return null;
  const counts = new Map<string, number>();
  for (const p of patterns) counts.set(p, (counts.get(p) ?? 0) + 1);
  let best: { pattern: string; count: number } | null = null;
  for (const [pattern, count] of counts) if (!best || count > best.count) best = { pattern, count };
  return best;
}

// ── Rows ────────────────────────────────────────────────────────────────────

export interface ScanOptions {
  /** Absolute first row of the window (= `grid.startRow`). */
  rowFrom: number;
  /** Last row of the used range (for `next_row` / `truncated`). */
  lastRow: number;
  includeBlank: boolean;
  /** Timeline detected on the top of the sheet by the executor when the window starts lower. */
  timeline?: ScanTimeline | null;
  maxChars?: number;
}

export function scanGrid(sheet: string, usedRange: string | null, grid: ScanGrid, opts: ScanOptions): ScanSheetOutput {
  const cols = grid.values[0]?.length ?? 0;
  const timeline = opts.timeline ?? detectTimeline(grid);
  const axisStart = timeline ? Math.max(0, Math.min(cols, letterIndex(timeline.first_col) - grid.startCol)) : -1;
  const axisEnd = timeline ? Math.max(axisStart, Math.min(cols - 1, letterIndex(timeline.last_col) - grid.startCol)) : cols - 1;
  const labelCol = detectLabelCol(grid, axisStart > 0 ? axisStart : 0);
  const unitCol = detectUnitCol(grid, labelCol, axisStart > 0 ? axisStart : cols);
  // Without a timeline the "axis" is everything right of the label/unit columns.
  const a0 = axisStart >= 0 ? axisStart : (unitCol ?? labelCol ?? -1) + 1;
  const a1 = axisEnd;

  const rows: ScanRow[] = [];
  const issues: ScanIssue[] = [];
  const otherRuns: ScanPeriodRun[] = [];
  const stats = { rows_scanned: grid.values.length, blank_rows: 0, input_rows: 0, calc_rows: 0, issues: 0 };
  let blankSpan: ScanRow | null = null;
  const pushIssue = (i: ScanIssue) => {
    stats.issues++;
    if (issues.length < MAX_ISSUES) issues.push(i);
  };
  const addr = (c: number, n: number) => `${colToLetter(grid.startCol + c)}${n}`;

  for (let r = 0; r < grid.values.length; r++) {
    const n = grid.startRow + r;
    const hidden = !!grid.rowHidden?.[r];
    const labelRaw = labelCol !== null ? grid.values[r]?.[labelCol] : null;
    const label = typeof labelRaw === 'string' && labelRaw.trim() ? labelRaw.trim().slice(0, 80) : null;
    const unitRaw = unitCol !== null ? grid.values[r]?.[unitCol] : null;
    const unit = typeof unitRaw === 'string' && unitRaw.trim() ? unitRaw.trim().slice(0, 20) : null;

    // Axis cells of the row.
    const axis: { c: number; v: CellValue; f: unknown; fmt: string }[] = [];
    for (let c = Math.max(0, a0); c <= a1; c++) {
      const cell = cellAt(grid, r, c);
      if (!isBlank(cell.v) || isFormula(cell.f)) axis.push({ c, ...cell });
    }
    const anyText = (grid.values[r] ?? []).some((v) => typeof v === 'string' && v.trim());

    // A second timeline / index run anywhere in the window (the header row itself is excluded). Integer
    // runs only count when they look like a period index or years (first value 0, 1 or ≥ 1900).
    if (!(timeline && n === timeline.header_row) && otherRuns.length < MAX_OTHER_RUNS) {
      const run = periodRun(grid, r);
      const firstValue = run ? grid.values[r]?.[run.start] : null;
      const indexLike = run?.kind !== 'numbers' || firstValue === 0 || firstValue === 1 || (typeof firstValue === 'number' && firstValue >= 1900);
      if (run && indexLike) otherRuns.push({ row: n, first_col: colToLetter(grid.startCol + run.start), last_col: colToLetter(grid.startCol + run.start + run.len - 1), periods: run.len });
    }

    if (!axis.length && !label && !anyText) {
      stats.blank_rows++;
      if (opts.includeBlank) {
        if (blankSpan && blankSpan.to === n - 1) blankSpan.to = n;
        else {
          blankSpan = { n, to: n, label: null, kind: 'blank' };
          rows.push(blankSpan);
        }
      }
      continue;
    }
    blankSpan = null;

    // Error values anywhere on the row.
    for (let c = 0; c < cols; c++) {
      const v = grid.values[r]?.[c];
      if (typeof v === 'string' && ERROR_RE.test(v)) {
        pushIssue({ kind: 'error_value', row: n, address: `${colToLetter(grid.startCol + c)}${n}`, detail: v });
      }
    }

    if (timeline && n === timeline.header_row) {
      rows.push({ n, label: label ?? 'timeline', kind: 'timeline', cells: axis.length, hidden });
      continue;
    }

    const formulas = axis.filter((x) => isFormula(x.f)).map((x) => x.f as string);
    const numbers = axis.filter((x) => typeof x.v === 'number');
    const textNumbers = axis.filter((x) => !isFormula(x.f) && isTextNumber(x.v));
    const firstCell = axis[0];
    const first: CellValue | undefined = firstCell ? (typeof firstCell.v === 'number' ? roundSig(firstCell.v) : firstCell.v) : undefined;
    const fmt = firstCell && firstCell.fmt !== 'General' ? firstCell.fmt : null;

    // Series statistics over the axis (input and formula rows alike).
    const series = (): Pick<ScanRow, 'gaps' | 'min' | 'max' | 'nonzero_from' | 'nonzero_to' | 'fmt_uniform'> => {
      const gaps = axis.length ? axis[axis.length - 1].c - axis[0].c + 1 - axis.length : 0;
      const nums = numbers.map((x) => x.v as number).filter((v) => Number.isFinite(v));
      const nonzero = numbers.filter((x) => x.v !== 0);
      const period = (x: { c: number }) => x.c - Math.max(0, a0) + 1;
      return {
        gaps,
        min: nums.length ? roundSig(Math.min(...nums)) : null,
        max: nums.length ? roundSig(Math.max(...nums)) : null,
        nonzero_from: nonzero.length ? period(nonzero[0]) : null,
        nonzero_to: nonzero.length ? period(nonzero[nonzero.length - 1]) : null,
        fmt_uniform: axis.every((x) => x.fmt === axis[0].fmt),
      };
    };
    // Numbers typed as text on the axis (one issue per row, first offending cell).
    if (textNumbers.length) {
      pushIssue({ kind: 'text_number', row: n, address: addr(textNumbers[0].c, n), detail: `"${String(textNumbers[0].v).slice(0, 20)}" stored as text (${textNumbers.length} cells)` });
    }
    const external = axis.find((x) => hasExternalRef(x.f));
    if (external) {
      pushIssue({ kind: 'external_link', row: n, address: addr(external.c, n), detail: String(external.f).slice(0, 80) });
    }

    if (!formulas.length) {
      if (numbers.length) {
        stats.input_rows++;
        rows.push({ n, label, unit, kind: 'input', pattern: 'const', exceptions: 0, first, fmt, cells: axis.length, hidden, ...series(), first_period_exception: false });
      } else {
        rows.push({ n, label, unit, kind: label || anyText ? 'header' : 'other', cells: axis.length, hidden });
      }
      continue;
    }

    const maj = majority(formulas)!;
    const exceptions = axis.length - maj.count; // literals and off-pattern formulas alike
    const firstOff = exceptions > 0 ? axis.find((x) => x.f !== maj.pattern) : undefined;
    const firstPeriodException = exceptions === 1 && !!firstOff && firstOff.c === Math.max(0, a0);
    let kind: ScanRowKind = 'calc';
    if (label && CHECK_LABEL_RE.test(label)) kind = 'check';
    else if (/\bSUM\(/i.test(maj.pattern) && (label ? TOTAL_LABEL_RE.test(label) : true)) kind = 'total';
    stats.calc_rows++;
    rows.push({ n, label, unit, kind, pattern: maj.pattern, exceptions, first, fmt, cells: axis.length, hidden, ...series(), first_period_exception: firstPeriodException });

    if (exceptions > 0) {
      pushIssue({ kind: 'broken_pattern', row: n, address: firstOff ? addr(firstOff.c, n) : `${n}`, detail: `${exceptions} of ${axis.length} cells differ from ${maj.pattern}` });
    }
    const lits = formulaLiterals(maj.pattern);
    if (lits.length && kind !== 'check') {
      pushIssue({ kind: 'literal_in_calc', row: n, address: addr(firstCell.c, n), detail: `literal ${lits.join(', ')} in ${maj.pattern}` });
    }
  }

  // Output budget: drop rows from the end and continue from there.
  const windowEnd = grid.startRow + grid.values.length - 1;
  let rowTo = windowEnd;
  const maxChars = opts.maxChars ?? SCAN_MAX_CHARS;
  const size = (rs: ScanRow[]) => JSON.stringify(rs).length + JSON.stringify(issues).length + 400;
  while (rows.length > 1 && size(rows) > maxChars) {
    const dropped = rows.pop()!;
    rowTo = dropped.n - 1;
  }
  const truncated = rowTo < opts.lastRow;
  return {
    sheet,
    used_range: usedRange,
    row_from: opts.rowFrom,
    row_to: rowTo,
    next_row: truncated ? rowTo + 1 : null,
    truncated,
    timeline,
    label_col: labelCol === null ? null : colToLetter(grid.startCol + labelCol),
    unit_col: unitCol === null ? null : colToLetter(grid.startCol + unitCol),
    rows: rows.filter((x) => x.n <= rowTo),
    issues: issues.filter((i) => i.row <= rowTo),
    other_period_runs: otherRuns.filter((x) => x.row <= rowTo),
    hidden_rows: hiddenSpans(grid.rowHidden?.slice(0, rowTo - grid.startRow + 1), grid.startRow, 'row'),
    stats,
  };
}

/** 1-based column index of a column letter (`A` → 1). */
function letterIndex(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** Result for a sheet with no used range. */
export function emptyScan(sheet: string, rowFrom: number): ScanSheetOutput {
  return {
    sheet, used_range: null, row_from: rowFrom, row_to: rowFrom - 1, next_row: null, truncated: false,
    timeline: null, label_col: null, unit_col: null, rows: [], issues: [], other_period_runs: [], hidden_rows: [],
    stats: { rows_scanned: 0, blank_rows: 0, input_rows: 0, calc_rows: 0, issues: 0 },
  };
}
